/**
 * sessionRefresh.js — Single-flight token refresh shared by BOTH the axios
 * interceptors and the `_kmRefreshWrapped` fetch hook.
 *
 * Design (2026-08-06 rebuild, requested 2026-08-16):
 *   • ONE in-flight refresh promise, keyed nowhere — a second caller who
 *     hits 401 while a refresh is running awaits the same promise instead
 *     of firing its own.  Two 401s in the same tick therefore produce ONE
 *     POST /api/auth/refresh, not two.
 *   • Success updates localStorage.accessToken + tokenExpiresAt and
 *     resolves with the new token.  Failure clears the promise so a
 *     subsequent 401 can try again once the user has re-authenticated
 *     via the login screen.
 *   • The refresh call uses the raw `axios` module (not the wrapped
 *     instance) so it is not itself intercepted.
 *
 * Public paths list is here (not inline in `_kmRefreshWrapped`) so both
 * the wrapper and any future hook can reuse it — the classifier must
 * skip any request whose path is in this list.
 */

import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// Paths that MUST bypass credential stamping AND refresh.  A 401 on any
// of these means "your identity is wrong right now" — refreshing and
// replaying would either bounce off the same wall or worse (a login
// POST replayed against `/api/auth/login` would double-submit).
const PUBLIC_API_PATH_PREFIXES = [
  '/api/auth/login',
  '/api/auth/register',
  '/api/auth/refresh',
  '/api/auth/forgot-password',
  '/api/auth/reset-password',
  '/api/auth/verify-reset-token',
  '/api/invite',
  '/api/join/',
  '/api/health',
  '/api/contractor-portal/',
  '/api/portal/',
];

export function isPublicApiPath(pathname) {
  if (!pathname) return false;
  return PUBLIC_API_PATH_PREFIXES.some((p) =>
    p.endsWith('/') ? pathname.startsWith(p) : pathname === p || pathname.startsWith(p + '/')
  );
}

// The tenant JWT lives under either key — see authedFile.js:44 for the
// same fallback pattern used everywhere in the app.  The classifier
// treats a bearer that matches EITHER stored value as the tenant's
// JWT; any other bearer is foreign and left alone.
export function currentTenantTokens() {
  try {
    return {
      accessToken: localStorage.getItem('accessToken') || null,
      legacyToken: localStorage.getItem('token') || null,
    };
  } catch (_e) {
    return { accessToken: null, legacyToken: null };
  }
}

// ------------------------------------------------------------------
// Single-flight refresh.
// ------------------------------------------------------------------
let _refreshFlight = null;

async function _doRefresh() {
  const refreshToken = typeof localStorage !== 'undefined'
    ? localStorage.getItem('refreshToken')
    : null;
  if (!refreshToken) throw new Error('No refresh token available');

  // Uses the raw axios module (not the wrapped axiosInstance) — same
  // call the pre-rebuild code made.  The request interceptor may stamp
  // Authorization on this call, but the backend authenticates by the
  // `refresh_token` in the body, so the header is irrelevant.
  const response = await axios.post(`${API_BASE}/api/auth/refresh`, {
    refresh_token: refreshToken,
  });

  if (!response.data || !response.data.success) {
    throw new Error('Token refresh failed');
  }
  const { access_token, expires_in } = response.data;
  try {
    localStorage.setItem('accessToken', access_token);
    const expiresAt = Date.now() + ((expires_in || 900) * 1000);
    localStorage.setItem('tokenExpiresAt', String(expiresAt));
    // Mirror to the legacy `token` key ONLY if it was already in use
    // (authedFile.js falls back to it).  Do not create the key if it
    // did not exist — that would resurrect a legacy pattern.
    if (localStorage.getItem('token')) {
      localStorage.setItem('token', access_token);
    }
  } catch (_e) { /* localStorage full/blocked — the caller still gets the token */ }
  return access_token;
}

/**
 * sharedRefresh() — awaited by both the axios interceptor and
 * `_kmRefreshWrapped`.  Concurrent callers share ONE HTTP round-trip.
 * Resolves with the new access token; rejects on any refresh failure.
 */
export function sharedRefresh() {
  if (_refreshFlight) return _refreshFlight;
  _refreshFlight = (async () => {
    try {
      const token = await _doRefresh();
      return token;
    } finally {
      _refreshFlight = null;
    }
  })();
  return _refreshFlight;
}

/**
 * classifyCredential({ url, headers }) — decides what to do with a
 * single outbound request.
 *
 *   'skip'    — never stamp, never refresh (public path, contractor
 *               session, or a foreign bearer we do not own).
 *   'tenant'  — the tenant's own JWT; a 401 must trigger sharedRefresh()
 *               and a single retry with the new bearer RESTAMPED.
 *
 * Skip by IDENTITY, not presence — see authedFile.js::_authedFetchBlob
 * which sets its own Authorization header from localStorage.  A
 * presence-based rule would exclude the one function every file
 * byte-fetch flows through.
 */
export function classifyCredential({ url, headers } = {}) {
  // 1) Public API path — always skip.
  let pathname = '';
  try {
    const u = new URL(url, typeof window !== 'undefined' ? window.location.origin : 'http://localhost');
    pathname = u.pathname || '';
  } catch (_e) {
    // Best-effort — if the URL is not parseable, be conservative and skip.
    return 'skip';
  }
  if (isPublicApiPath(pathname)) return 'skip';

  // 2) Contractor portal session — skip.  These are portal identities
  // that flow through their own axios instance in ContractorPortal.js
  // and must not be shoved onto the tenant refresh path.
  const hasContractor = _headerValue(headers, 'x-contractor-session');
  if (hasContractor) return 'skip';

  // 3) Bearer identity.
  const authz = _headerValue(headers, 'authorization');
  if (!authz) return 'skip';
  const m = /^\s*Bearer\s+(.+)\s*$/i.exec(authz);
  if (!m) return 'skip';
  const bearer = m[1];

  const { accessToken, legacyToken } = currentTenantTokens();
  if (bearer && (bearer === accessToken || bearer === legacyToken)) return 'tenant';
  return 'skip';
}

// Header lookup that tolerates both a plain object and a Headers
// instance (fetch accepts either in `init.headers`, and a Request
// exposes them via a Headers instance on `req.headers`).
function _headerValue(headers, name) {
  if (!headers) return null;
  const lc = name.toLowerCase();
  try {
    if (typeof headers.get === 'function') {
      return headers.get(name) || headers.get(lc) || null;
    }
  } catch (_e) { /* fall through */ }
  if (typeof headers === 'object') {
    for (const k of Object.keys(headers)) {
      if (k.toLowerCase() === lc) return headers[k];
    }
  }
  return null;
}
